import { FieldGuideUi } from '@tcc/field-guide';
import { useLoggedInUser } from '@tcc/shared-data-lib';
import React, { Suspense, useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useBoot } from '../../boot';
import { bootSteps } from '../../boot/bootSteps';
import { useAppRouterLoadVital } from '../../hooks/useAppRouterLoadVital';
import { SpinnerLoading } from '../../components/SpinnerLoading/SpinnerLoading';
import { AUTH_VERIFICATION_PATH, FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY } from '../../constants/firstLoginVideo';
import { ROUTE } from '../../constants/routes';
import { AuthPluginContainer } from '../../containers/AuthPluginContainer';
import { CropPlanningAccessProvider } from '../../contexts/CropPlanningAccessContext';
import { getURLFromHash } from '../../utils/env';
import { injectCookieConsentBanner } from '../../utils/injectCookieConsentBanner';
import { useUserLoggedInConfigs, withUseQueryConfig } from '../../utils/sharedData';
import { markManualLoginRequired, setInitialAuthStatus } from '../../utils/appTotalLoad';
import { getUserData, logout } from '../../utils/wam';
import { ApplicationView } from '../ApplicationView';
import { AuthorizedRoute } from '../AuthorizedRoute';

/*
 During react-router's initialization, it calls window.location.replace('#/')
 if we don't have an initial hash.
 During release, we add a <base> tag to the head of the index.html file that specifies
 the commit hash that is to be released. Using replace() with a relative path
 also adds the base path to the end of the url, which we do not want. So, we set the
 hash path manually so react-router does not unintentionally redirect us to the wrong path.
*/
if (window.location.hash === '') {
  window.location.hash = '/';
}

export const AppRouter = () => {
  const location = useLocation();
  const { pathname } = location;
  const isOnAuth = pathname.includes(ROUTE.Auth);
  const hasPreso = getUserData();

  const bootState = useBoot(bootSteps);

  useEffect(() => {
    injectCookieConsentBanner();
  }, []);

  useEffect(() => {
    if (pathname.startsWith(AUTH_VERIFICATION_PATH)) {
      sessionStorage.setItem(FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY, 'true');
    }
  }, [pathname]);

  // Capture whether a valid session existed at cold load. Used to flag the
  // end-to-end app_total_load vital so dashboards can exclude manual-login loads
  // (the login form's human think-time). Only the first call takes effect.
  useEffect(() => {
    setInitialAuthStatus(Boolean(hasPreso));
  }, [hasPreso]);

  const useLoggedInUserArgs = {
    ...useUserLoggedInConfigs,
    config: {
      ...useUserLoggedInConfigs.config,
      skip: isOnAuth || !hasPreso
    }
  };
  const { user, error: userError, isLoading } = useLoggedInUser(useLoggedInUserArgs);

  // Temporary dual-hook call to warm React Query cache while SWR is still in use.
  // Remove this useQuery priming call after SWR is fully removed.
  useLoggedInUser(withUseQueryConfig(useLoggedInUserArgs));

  const history = useHistory();

  // Build auth redirect — always preserves the current URL so tcc-auth-plugin
  // can document.location.assign() back after login.
  const authRedirect = `${ROUTE.Auth}/login?to=${encodeURIComponent(window.location.href)}`;

  // Stage 4 gate: the AppRouter spinner shows until both i18n boot and the
  // logged-in user resolve. Track its full duration (boot + user).
  const isRouterLoading = isLoading || bootState.isBooting;
  useAppRouterLoadVital(isRouterLoading);

  // Scope html tag with app name as class to avoid css leaks between plugins.
  useEffect(() => {
    const currentMainApp = Object.values(ROUTE).find(appPath => pathname.includes(appPath));

    if (currentMainApp) {
      const htmlNewClassName = currentMainApp.replace('/', '');

      const htmlTag = document.getElementsByTagName('html')[0];

      // Clear previous classes
      htmlTag.classList.remove(...htmlTag.classList);

      htmlTag.classList.add(htmlNewClassName);
    }
  }, [pathname]);

  useEffect(() => {
    if (userError && !isOnAuth) {
      // A session appeared present but was rejected — the user must log in
      // manually, so flag this load out of the clean app_total_load cohort.
      markManualLoginRequired();
      logout();
      history.push(authRedirect);
    }
  }, [userError, isOnAuth, history, authRedirect]);

  if (isRouterLoading) {
    return <SpinnerLoading />;
  }

  return (
    <Suspense fallback={<SpinnerLoading />}>
      <FieldGuideUi userUUID={user?.details.uuid || ''} />
      <Switch>
        <Route path={ROUTE.Auth} component={AuthPluginContainer} />
        {!isOnAuth && !!hasPreso && (
          <CropPlanningAccessProvider>
            <AuthorizedRoute component={ApplicationView} redirect={authRedirect} isEnabled={!!user} />
          </CropPlanningAccessProvider>
        )}
        <Route path='*'>
          {() => {
            window.location.href = getURLFromHash(authRedirect);
            return null;
          }}
        </Route>
      </Switch>
    </Suspense>
  );
};
